(function() {
    'use strict';

    class ClickableTurmite {
        constructor(options = {}) {
            // Configuration
            this.speed = options.speed || 50;
            this.maxInstances = options.maxInstances || 10;
            this.colors = options.colors || ['#8B5A2B', '#C67E3B', '#D4A574', '#E6B88A'];
            this.darkColors = options.darkColors || ['#DE7356', '#FF8C69', '#FFA07A', '#FFB59B'];
            this.opacity = options.opacity || 0.25;
            this.lifespan = options.lifespan || 30000; // 30 seconds
            this.gridSize = options.gridSize || 101;
            this.center = Math.floor(this.gridSize / 2);
            
            // State
            this.turmites = [];
            this.stepCount = 0;
            this.animationId = null;
            
            // Create hint element
            this.createHintElement();
            
            // Bind click handler
            this.handleClick = this.handleClick.bind(this);
            document.addEventListener('click', this.handleClick);
            
            // Start cleanup interval
            this.startCleanup();
            
            // Start animation loop
            this.animate();
        }
        
        createHintElement() {
            this.hintElement = document.createElement('div');
            this.hintElement.className = 'turmite-hint';
            this.hintElement.textContent = '◈ click anywhere to spawn a turmite ◈';
            this.hintElement.style.cssText = `
                position: fixed;
                bottom: 20px;
                left: 50%;
                transform: translateX(-50%);
                font-family: 'Courier Prime', 'Courier New', monospace;
                font-size: 0.75rem;
                color: #666;
                opacity: 0.7;
                pointer-events: none;
                z-index: 100;
                text-align: center;
                animation: turmitePulse 2s ease-in-out infinite;
            `;
            
            // Add animation style if not exists
            if (!document.querySelector('#turmite-animations')) {
                const style = document.createElement('style');
                style.id = 'turmite-animations';
                style.textContent = `
                    @keyframes turmitePulse {
                        0%, 100% { opacity: 0.7; }
                        50% { opacity: 0.3; }
                    }
                `;
                document.head.appendChild(style);
            }
            
            document.body.appendChild(this.hintElement);
        }
        
        updateHintVisibility() {
            if (this.hintElement) {
                this.hintElement.style.display = this.turmites.length === 0 ? 'block' : 'none';
            }
        }
        
        handleClick(event) {
            // Don't spawn if clicking on controls, buttons, or links
            const target = event.target;
            if (target.closest('button') || 
                target.closest('input') || 
                target.closest('a') ||
                target.closest('.gh-theme-toggle') ||
                target.closest('form')) {
                return;
            }
            
            // Remove oldest if at max capacity
            if (this.turmites.length >= this.maxInstances) {
                const oldest = this.turmites.shift();
                if (oldest.element) {
                    oldest.element.remove();
                }
            }
            
            // Create new turmite
            const newTurmite = {
                id: `turmite-${Date.now()}-${Math.random()}`,
                x: this.center,
                y: this.center,
                direction: Math.floor(Math.random() * 4),
                grid: this.createGrid(),
                birthTime: Date.now(),
                startX: event.clientX,
                startY: event.clientY,
                element: this.createTurmiteElement(event.clientX, event.clientY)
            };
            
            this.turmites.push(newTurmite);
            this.updateHintVisibility();
        }
        
        createGrid() {
            return Array(this.gridSize).fill(null).map(() => 
                Array(this.gridSize).fill(false)
            );
        }
        
        createTurmiteElement(x, y) {
            const element = document.createElement('div');
            element.className = 'clickable-turmite';
            element.style.cssText = `
                position: fixed;
                top: ${y}px;
                left: ${x}px;
                transform: translate(-50%, -50%);
                font-family: 'Courier Prime', 'Courier New', monospace;
                font-size: 0.4rem;
                line-height: 0.4rem;
                color: ${this.getColor(this.turmites.length)};
                opacity: ${this.opacity};
                white-space: pre;
                filter: blur(0.3px);
                pointer-events: none;
                z-index: 1;
                width: ${this.gridSize * 0.4}rem;
                height: ${this.gridSize * 0.4}rem;
                overflow: hidden;
                transition: opacity 1s ease-out;
            `;
            
            const innerDiv = document.createElement('div');
            innerDiv.style.cssText = `
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                text-align: center;
            `;
            
            element.appendChild(innerDiv);
            document.body.appendChild(element);
            return element;
        }
        
        getColor(index) {
            // Use CSS variables for consistent theming
            const colorIndex = (index % 4) + 1;
            const colorVar = `var(--turmite-color-${colorIndex})`;
            return colorVar;
        }
        
        updateTurmite(turmite) {
            const { x, y, direction, grid } = turmite;
            const newGrid = grid.map(row => [...row]);
            
            // Current cell state
            const currentCell = grid[y][x];
            
            // Distance from center for spiral behavior
            const dx = x - this.center;
            const dy = y - this.center;
            const distanceFromCenter = Math.sqrt(dx * dx + dy * dy);
            const angleFromCenter = Math.atan2(dy, dx);
            
            // Langton's ant rules with spiral variations
            let newDirection;
            if (currentCell) {
                // On white, turn right with spiral tendency
                const spiralForce = Math.sin(angleFromCenter + distanceFromCenter * 0.1) > 0 ? 1 : 0;
                newDirection = (direction + 1 + spiralForce) % 4;
                newGrid[y][x] = false;
            } else {
                // On black, turn left with stronger spiral
                const spiralBias = this.stepCount % 30 < 5 ? 1 : 0;
                const spiralForce = Math.cos(angleFromCenter + distanceFromCenter * 0.05) > 0 ? 1 : -1;
                newDirection = (direction + 3 + spiralBias + spiralForce) % 4;
                newGrid[y][x] = true;
            }
            
            // Add randomness for organic patterns
            const edgeFactor = Math.min(distanceFromCenter / (this.gridSize * 0.4), 1);
            if (Math.random() < 0.02 * (1 - edgeFactor * 0.5)) {
                newDirection = Math.floor(Math.random() * 4);
            }
            
            // Move forward
            let newX = x;
            let newY = y;
            switch (newDirection) {
                case 0: newY = y - 1; break; // up
                case 1: newX = x + 1; break; // right
                case 2: newY = y + 1; break; // down
                case 3: newX = x - 1; break; // left
            }
            
            // Wrap around edges
            if (newX < 0) newX = this.gridSize - 1;
            if (newX >= this.gridSize) newX = 0;
            if (newY < 0) newY = this.gridSize - 1;
            if (newY >= this.gridSize) newY = 0;
            
            // Update turmite state
            turmite.x = newX;
            turmite.y = newY;
            turmite.direction = newDirection;
            turmite.grid = newGrid;
            
            return turmite;
        }
        
        countNeighbors(grid, x, y) {
            let count = 0;
            for (let dy = -1; dy <= 1; dy++) {
                for (let dx = -1; dx <= 1; dx++) {
                    if (dx === 0 && dy === 0) continue;
                    const nx = (x + dx + this.gridSize) % this.gridSize;
                    const ny = (y + dy + this.gridSize) % this.gridSize;
                    if (grid[ny][nx]) count++;
                }
            }
            return count;
        }
        
        renderGrid(grid, turmiteX, turmiteY) {
            return grid.map((row, y) => 
                row.map((cell, x) => {
                    if (x === turmiteX && y === turmiteY) return '◉';
                    if (cell) {
                        const neighbors = this.countNeighbors(grid, x, y);
                        if (neighbors >= 6) return '※';
                        if (neighbors >= 4) return '◈';
                        if (neighbors >= 2) return '∴';
                        return '·';
                    }
                    return ' ';
                }).join('')
            ).join('\n');
        }
        
        animate() {
            // Update all turmites
            this.turmites.forEach((turmite, index) => {
                this.updateTurmite(turmite);
                
                // Update visual
                if (turmite.element) {
                    const innerDiv = turmite.element.querySelector('div');
                    if (innerDiv) {
                        innerDiv.textContent = this.renderGrid(turmite.grid, turmite.x, turmite.y);
                    }
                    
                    // Update color for theme changes
                    turmite.element.style.color = this.getColor(index);
                    
                    // Update opacity based on age
                    const age = Date.now() - turmite.birthTime;
                    const fadeOut = this.lifespan > 0 ? Math.max(0, 1 - (age / this.lifespan)) : 1;
                    turmite.element.style.opacity = this.opacity * fadeOut;
                }
            });
            
            this.stepCount++;
            
            // Continue animation
            this.animationId = requestAnimationFrame(() => {
                setTimeout(() => this.animate(), this.speed);
            });
        }
        
        startCleanup() {
            this.cleanupInterval = setInterval(() => {
                const now = Date.now();
                this.turmites = this.turmites.filter(turmite => {
                    const age = now - turmite.birthTime;
                    if (age >= this.lifespan) {
                        if (turmite.element) {
                            turmite.element.remove();
                        }
                        return false;
                    }
                    return true;
                });
                this.updateHintVisibility();
            }, 1000);
        }
        
        destroy() {
            // Remove event listener
            document.removeEventListener('click', this.handleClick);
            
            // Clear intervals
            if (this.cleanupInterval) {
                clearInterval(this.cleanupInterval);
            }
            
            // Cancel animation
            if (this.animationId) {
                cancelAnimationFrame(this.animationId);
            }
            
            // Remove all turmite elements
            this.turmites.forEach(turmite => {
                if (turmite.element) {
                    turmite.element.remove();
                }
            });
            
            // Remove hint
            if (this.hintElement) {
                this.hintElement.remove();
            }
        }
    }
    
    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            window.clickableTurmite = new ClickableTurmite();
        });
    } else {
        window.clickableTurmite = new ClickableTurmite();
    }
    
    // Clean up on page unload
    window.addEventListener('beforeunload', () => {
        if (window.clickableTurmite) {
            window.clickableTurmite.destroy();
        }
    });
})();